import React, { useCallback, useState } from "react"

import {
  uploadAudioFileWithSignedPost,
  useAudioUploadUrlTransport,
} from "@/audio"
import {
  Button,
  Card,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@coval/ui"
import { Music } from "lucide-react"

import { TestSetType, type TestSetInput } from "@/types/TestSetInput"
import { cn } from "@/utils/cn"

import AudioDropZone from "./AudioDropZone"
import AudioFileRow from "./AudioFileRow"
import {
  getFileExtension,
  getMimeType,
  validateAudioFile,
  type AudioFile,
} from "./types"

interface AudioUploadModalProps {
  onTestCasesProcessed: (testSetInput: TestSetInput) => Promise<void>
  triggerClassName?: string
}

const AudioUploadModal: React.FC<AudioUploadModalProps> = ({
  onTestCasesProcessed,
  triggerClassName,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [audioFiles, setAudioFiles] = useState<AudioFile[]>([])
  const [isDragging, setIsDragging] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [globalError, setGlobalError] = useState<string | null>(null)
  const [expandedTranscripts, setExpandedTranscripts] = useState<Set<string>>(
    new Set(),
  )

  const getUploadUrl = useAudioUploadUrlTransport()

  const resetState = () => {
    audioFiles.forEach((af) => {
      if (af.previewUrl) {
        URL.revokeObjectURL(af.previewUrl)
      }
    })
    setAudioFiles([])
    setIsDragging(false)
    setIsSubmitting(false)
    setGlobalError(null)
    setExpandedTranscripts(new Set())
  }

  const uploadFile = useCallback(
    async (audioFile: AudioFile) => {
      const ext = getFileExtension(audioFile.file.name)
      const mimeType = getMimeType(ext)

      try {
        setAudioFiles((prev) =>
          prev.map((af) =>
            af.id === audioFile.id
              ? { ...af, status: "uploading", progress: 10 }
              : af,
          ),
        )

        const { url, fields, s3Key, s3Bucket, maxSizeBytes } =
          await getUploadUrl.mutateAsync({
            fileType: mimeType,
            fileExtension: ext,
          })

        setAudioFiles((prev) =>
          prev.map((af) =>
            af.id === audioFile.id ? { ...af, progress: 40 } : af,
          ),
        )

        const uploadResponse = await uploadAudioFileWithSignedPost({
          url,
          fields,
          file: audioFile.file,
          maxSizeBytes,
        })

        if (!uploadResponse.ok) {
          throw new Error(`Upload failed with status ${uploadResponse.status}`)
        }

        setAudioFiles((prev) =>
          prev.map((af) =>
            af.id === audioFile.id
              ? { ...af, status: "done", progress: 100, s3Key, s3Bucket }
              : af,
          ),
        )
      } catch (err) {
        setAudioFiles((prev) =>
          prev.map((af) =>
            af.id === audioFile.id
              ? {
                  ...af,
                  status: "error",
                  progress: 0,
                  error: err instanceof Error ? err.message : "Upload failed",
                }
              : af,
          ),
        )
      }
    },
    [getUploadUrl],
  )

  const addFiles = useCallback(
    (files: FileList | File[]) => {
      const fileArray = Array.from(files)
      const newAudioFiles: AudioFile[] = []

      for (const file of fileArray) {
        const validationError = validateAudioFile(file)
        if (validationError) {
          setGlobalError(validationError)
          continue
        }

        newAudioFiles.push({
          file,
          id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
          status: "pending",
          progress: 0,
          previewUrl: URL.createObjectURL(file),
        })
      }

      if (newAudioFiles.length > 0) {
        setGlobalError(null)
        setAudioFiles((prev) => [...prev, ...newAudioFiles])
        newAudioFiles.forEach((af) => {
          void uploadFile(af)
        })
      }
    },
    [uploadFile],
  )

  const removeFile = (id: string) => {
    setAudioFiles((prev) => {
      const file = prev.find((af) => af.id === id)
      if (file?.previewUrl) {
        URL.revokeObjectURL(file.previewUrl)
      }
      return prev.filter((af) => af.id !== id)
    })
    setExpandedTranscripts((prev) => {
      const next = new Set(prev)
      next.delete(id)
      return next
    })
  }

  const toggleTranscript = (id: string) => {
    setExpandedTranscripts((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  const updateTranscript = (id: string, text: string) => {
    setAudioFiles((prev) =>
      prev.map((af) =>
        af.id === id ? { ...af, groundTruthTranscript: text } : af,
      ),
    )
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(true)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      addFiles(e.dataTransfer.files)
    }
  }

  const allDone =
    audioFiles.length > 0 && audioFiles.every((af) => af.status === "done")

  const handleSubmit = async (): Promise<boolean> => {
    if (!allDone) {
      return false
    }

    setIsSubmitting(true)
    try {
      await onTestCasesProcessed({
        display_name: "",
        description: "Audio test cases",
        test_set_metadata: { owner: "" },
        testCases: audioFiles.map((af) => ({
          input_str: af.file.name,
          expected_behaviors: [],
          s3_key: af.s3Key || null,
          s3_bucket: af.s3Bucket || null,
          input_type: TestSetType.AUDIO,
          expected_output_json: af.groundTruthTranscript
            ? { ground_truth_transcript: af.groundTruthTranscript }
            : null,
        })),
        test_set_type: TestSetType.SCENARIO,
      })
      return true
    } catch (err) {
      setGlobalError(
        err instanceof Error ? err.message : "Failed to submit test cases",
      )
      return false
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <>
      <Card
        interactive
        className={cn("flex items-center gap-3 px-4 py-3", triggerClassName)}
        onClick={() => setIsOpen(true)}
      >
        <Music className="w-4 h-4 text-coval-text-muted flex-shrink-0" />
        Upload audio to test against
      </Card>
      <Dialog
        open={isOpen}
        onOpenChange={(open) => {
          setIsOpen(open)
          if (!open) {
            resetState()
          }
        }}
      >
        <DialogContent size="lg">
          <DialogHeader>
            <DialogTitle>Upload Audio Test Cases</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <div className="space-y-4">
              <p className="text-sm text-coval-text-muted">
                Upload audio files to create test cases. Each file becomes a
                separate test case with input type AUDIO.
              </p>

              <AudioDropZone
                isDragging={isDragging}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onFilesSelected={addFiles}
              />

              {globalError && (
                <div className="bg-coval-danger-surface border border-coval-danger-border rounded-lg p-3">
                  <p className="text-sm text-coval-danger-on-surface">
                    {globalError}
                  </p>
                </div>
              )}

              {audioFiles.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-coval-text-muted mb-2">
                    {audioFiles.length} file
                    {audioFiles.length !== 1 ? "s" : ""} selected
                  </p>
                  <div className="max-h-64 overflow-y-auto space-y-2">
                    {audioFiles.map((af) => (
                      <AudioFileRow
                        key={af.id}
                        audioFile={af}
                        isTranscriptExpanded={expandedTranscripts.has(af.id)}
                        onRemove={() => removeFile(af.id)}
                        onToggleTranscript={() => toggleTranscript(af.id)}
                        onTranscriptChange={(text) =>
                          updateTranscript(af.id, text)
                        }
                        onError={setGlobalError}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          </DialogBody>
          <DialogFooter>
            <Button
              variant="secondary"
              onClick={() => {
                setIsOpen(false)
                resetState()
              }}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                void (async () => {
                  const success = await handleSubmit()
                  if (success) {
                    setIsOpen(false)
                    resetState()
                  }
                })()
              }}
              disabled={!allDone || isSubmitting}
            >
              Add test cases
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

export default AudioUploadModal
